import {Component, effect, inject, OnDestroy, OnInit} from '@angular/core';
import {MenuComponent, MenuModel} from '@shared/components/menu';
import {AuthenticationService} from '@core/auth';
import {CompanyListDataModel, CompanyListModel, CompanyService, User, UserService} from '@core/services';
import {catchError, concatMap, filter, map} from 'rxjs/operators';
import {EMPTY, of, Subject, Subscription} from 'rxjs';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {PusherJsService} from '@services/pusher-js/pusher-js.service';
import {UUIDType} from '@shared/models/uuid/uuid.model';
import {AuthService} from '@auth0/auth0-angular';
import {BriefViewService} from '../briefs/briefs-view/brief-view.service';
import {toSignal} from '@angular/core/rxjs-interop';
import {isActiveUserSession, removeVisibleEvent} from './user.session';
import {getDecodedTokenPermissions, MenuListPermission, updateCurrentUser} from './home.utils';
import {OctDocumentService} from '@services/document/document.service';
import {SidenavComponent} from '@shared/components/sidenav';
import {AsyncPipe} from '@angular/common';
import {DownloadHistoryNotifications} from '@pages/home/download-history/download-history.directive';
import {GlobalNotificationsDirective} from '@pages/home/global-notifications/global-notifications.directive';
import {COMMON_DATA_PROVIDERS} from '@core/providers/common-data/common-data';
import {RealtimeChannelService, UserContextService} from '@pages/home/pusher-listener/pusher-listener.service';

@Component({
  selector: 'oct-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  imports: [SidenavComponent, MenuComponent, RouterOutlet, AsyncPipe],
  hostDirectives: [DownloadHistoryNotifications, GlobalNotificationsDirective],
  providers: [RealtimeChannelService, UserContextService, COMMON_DATA_PROVIDERS]
})
export class HomePageComponent extends MenuListPermission implements OnInit, OnDestroy {
  private readonly _companyService = inject(CompanyService);
  private readonly _router = inject(Router);
  private readonly _pusherService = inject(PusherJsService);
  private readonly _briefViewService = inject(BriefViewService);

  sidenavOpenedState = true;
  sidenavChangeState = true;
  isVisible = true;
  companies: CompanyListModel[] = [];
  company!: CompanyListModel;
  sidenavState = '';
  companyPrefix = inject(OctDocumentService).activeCompanyPrefix();
  isIntercomOn = new Subject<boolean>();
  authenticationService = inject(AuthenticationService);
  userService = inject(UserService);
  auth = inject(AuthService);
  user = toSignal(updateCurrentUser(this.userService), {initialValue: {} as User});
  logout = this.auth.getAccessTokenSilently().pipe(
    catchError(() => {
      this.authenticationService.logout();
      return EMPTY;
    }),
    map(token => {
      if (!token) {
        this.authenticationService.logout();
      }
    })
  );
  isUploadApprovals = toSignal(
    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects),
      map(url => url.includes('upload-approvals') || url.includes('add-new-version'))
    )
  );
  private readonly _subs = new Subscription();

  constructor() {
    super();
    effect(() => {
      const allowDashboard = this.isAllowedDashboard();
      const allowLanguage = this.isAllowedLanguageDashboard();
      this._updateMenuPermissions({allowDashboard, allowLanguage});
    });
    isActiveUserSession();
    this._updatePermission();
    this._setUnreadNotificationsCount();
  }

  ngOnInit() {
    this._updateMenuPermissions();
    this._getCompany();
    this._onCompanyListChange();
    this._pusherService.init();
  }

  ngOnDestroy() {
    removeVisibleEvent();
    this._subs.unsubscribe();
    this._briefViewService.resetSearchTerm();
  }

  changeCompany(id: UUIDType) {
    if (id && this.companies?.length > 0) {
      this.company = this._selectedCompany(this.companyPrefix);
      this._companyService.currentCompany.next(this.company || this.companies[0]);
    }
  }

  sidenavOpenedStateChange(state: boolean) {
    this.sidenavOpenedState = state;
    this.isVisible = !this.isVisible;
  }

  sidenavStateChange(state: boolean) {
    this.sidenavChangeState = state;
  }

  getSidenavState(state: string) {
    this.sidenavState = state;
  }

  menuOnChange(modelEvt: MenuModel) {
    if (modelEvt?.routerLink === '/briefs') {
      this._briefViewService.resetSearchTerm();
    }

    if (modelEvt?.routerLink && this.sidenavState === 'over') {
      this.sidenavStateChange(false);
    }
  }

  private _onCompanyListChange() {
    this._subs.add(
      this._companyService.companyListChange
        .pipe(concatMap(isChange => (isChange ? this._companyService.getCompanyList() : of([]))))
        .subscribe((company: CompanyListDataModel) => this._checkCompanyData(company))
    );
  }

  private _getCompany() {
    this._subs.add(this._companyService.getCompanyList().subscribe(companies => this._checkCompanyData(companies)));
  }

  private _checkCompanyData(companies: CompanyListDataModel) {
    if (companies?.data?.length > 0) {
      this.companies = companies.data;
      const selectedCompany = this._selectedCompany(this.companyPrefix);
      if (selectedCompany?.attributes?.isIntercom) {
        this.isIntercomOn.next(true);
      }
      this.changeCompany(selectedCompany?.id ?? companies.data[0].id);
    }
  }

  private _updatePermission() {
    const permissions = getDecodedTokenPermissions();
    if (permissions.length > 0) {
      this.permissionService.permission.next(permissions);
    }
  }

  private _selectedCompany(urlPrefix: string): CompanyListModel {
    return this.companies.find(company => company.attributes.urlPrefix === urlPrefix) ?? this.companies[0];
  }
}
